import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTenant } from '../TenantContext';
import { useTheme } from '../ThemeContext';
import { useAuth } from '../context/AuthContext';
import { SearchSelect } from './UI';
import { Building2, ChevronDown, ChevronsLeft, ChevronsRight, Sun, Moon, Menu, LogOut, User, KeyRound, X } from 'lucide-react';

// Route yang menampilkan tenant selector (Tenant page + semua child menu Radius + VPN yang per-tenant)
const TENANT_SELECTOR_ROUTES = ['/tenants', '/users', '/profiles', '/nas', '/online-users', '/vpn/wireguard', '/vpn/ipsec'];

/* ── Change Password Modal ── */
function ChangePasswordModal({ onClose }) {
  const { changePassword, logout } = useAuth();
  const [cur, setCur]   = useState('');
  const [next, setNext] = useState('');
  const [nextConfirm, setNextConfirm] = useState('');
  const [err, setErr]   = useState('');
  const [ok,  setOk]    = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!cur || !next || !nextConfirm) return setErr('Semua field wajib diisi.');
    if (next.length < 8) return setErr('Password baru minimal 8 karakter.');
    if (next !== nextConfirm) return setErr('Konfirmasi password baru tidak cocok.');
    setBusy(true); setErr('');
    try {
      await changePassword(cur, next);
      setOk(true);
      // password berubah -> token lama tidak valid lagi, logout otomatis
      setTimeout(() => { onClose(); logout(); }, 1500);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const inp = (label, val, set, type='text') => (
    <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
      <label style={{ fontSize:11, fontWeight:500, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:'0.04em' }}>{label}</label>
      <input type={type} value={val} onChange={e => { set(e.target.value); setErr(''); }}
        style={{ background:'var(--bg-input)', border:'1px solid var(--border)', borderRadius:'var(--radius)', color:'var(--text-primary)', fontSize:13, padding:'9px 11px', outline:'none', fontFamily:'var(--font-sans)' }}
        onFocus={e=>e.target.style.borderColor='var(--accent-blue)'}
        onBlur={e=>e.target.style.borderColor='var(--border)'}
      />
    </div>
  );

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}
      onClick={onClose}>
      <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:28, width:340, boxShadow:'var(--shadow)' }}
        onClick={e=>e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ fontSize:16, fontWeight:600, color:'var(--text-primary)', margin:0 }}>Ganti Password</h3>
          <button onClick={onClose} style={{ background:'none', border:'none', color:'var(--text-muted)', cursor:'pointer', display:'flex' }}><X size={18}/></button>
        </div>
        {ok
          ? <div style={{ color:'var(--accent-green)', fontSize:13, textAlign:'center', padding:'16px 0' }}>✓ Password berhasil diubah! Anda akan logout otomatis…</div>
          : <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:14 }}>
              {inp('Password Sekarang', cur, setCur, 'password')}
              {inp('Password Baru', next, setNext, 'password')}
              {inp('Konfirmasi Password Baru', nextConfirm, setNextConfirm, 'password')}
              {err && <div style={{ fontSize:12, color:'var(--accent-red)' }}>{err}</div>}
              <button type="submit" disabled={busy} style={{ background:'var(--accent-blue)', border:'none', borderRadius:'var(--radius)', color:'#fff', fontSize:13, fontWeight:600, padding:'10px 0', cursor:busy?'not-allowed':'pointer', marginTop:4 }}>
                {busy ? 'Menyimpan…' : 'Simpan'}
              </button>
            </form>
        }
      </div>
    </div>
  );
}

/* ── Topbar ── */
export default function Topbar({ onMenuClick, collapsed, onToggleCollapse }) {
  const { tenants, selectedTenant, selectTenant, currentTenant } = useTenant();
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const location = useLocation();

  const [menuOpen, setMenuOpen]   = useState(false);
  const [showPwd,  setShowPwd]    = useState(false);
  const menuRef = useRef(null);

  // Tampilkan tenant selector hanya di halaman Tenant dan child menu Radius
  const showTenantSelector = TENANT_SELECTOR_ROUTES.includes(location.pathname);

  useEffect(() => {
    const handler = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <>
      {showPwd && <ChangePasswordModal onClose={() => setShowPwd(false)} />}

      <div style={{
        height: 48, background: 'var(--bg-secondary)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', gap: 10, flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={onMenuClick} className="mobile-menu-btn"
            style={{ display: 'none', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 4 }}>
            <Menu size={20} />
          </button>

          {/* Collapse/expand sidebar (desktop only) */}
          <button onClick={onToggleCollapse} className="desktop-collapse-btn" title={collapsed ? 'Buka sidebar' : 'Tutup sidebar'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
              color: 'var(--text-secondary)', cursor: 'pointer', padding: 5, transition: 'border-color 0.15s, color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent-blue)'; e.currentTarget.style.color = 'var(--accent-blue)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          >
            {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
          </button>

          {showTenantSelector && (<>
          <Building2 size={14} color="var(--text-muted)" style={{ flexShrink: 0 }} />
          <span className="hide-xs" style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>Tenant:</span>
          <div style={{ width: 190 }}>
            <SearchSelect
              value={selectedTenant || ''}
              onChange={id => selectTenant(id || '')}
              options={tenants}
              getKey={t => t.id}
              getLabel={t => t.name}
              getSub={t => t.realm}
              placeholder="— Pilih Tenant —"
              allowClear
              clearLabel="— Pilih Tenant —"
              defaultCount={10}
              buttonStyle={{ padding: '5px 10px', fontSize: 12 }}
            />
          </div>
          {currentTenant && (
            <span className="hide-sm" style={{
              fontSize: 11, padding: '2px 8px',
              background: 'rgba(59,130,246,0.1)', color: 'var(--accent-blue)',
              borderRadius: 20, border: '1px solid rgba(59,130,246,0.25)', whiteSpace: 'nowrap',
            }}>
              {currentTenant.status === 'active' ? '● active' : '○ inactive'}
            </span>
          )}
          </>)}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Theme toggle */}
          <button onClick={toggle}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0,
              padding: '5px 10px', borderRadius: 'var(--radius)',
              background: 'var(--bg-input)', border: '1px solid var(--border)',
              color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer',
              transition: 'border-color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-blue)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
          >
            {theme === 'dark'
              ? <><Sun size={13} color="var(--accent-orange)" /><span className="hide-xs">Light</span></>
              : <><Moon size={13} color="var(--accent-blue)" /><span className="hide-xs">Dark</span></>
            }
          </button>

          {/* User menu */}
          <div ref={menuRef} style={{ position: 'relative' }}>
            <button onClick={() => setMenuOpen(o => !o)}
              style={{
                display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0,
                padding: '5px 10px', borderRadius: 'var(--radius)',
                background: menuOpen ? 'var(--bg-card-hover)' : 'var(--bg-input)',
                border: `1px solid ${menuOpen ? 'var(--accent-blue)' : 'var(--border)'}`,
                color: 'var(--text-primary)', fontSize: 12, cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--accent-blue), var(--accent-cyan))',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <User size={12} color="#fff" />
              </div>
              <span className="hide-xs" style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.username || 'Admin'}
              </span>
              <ChevronDown size={11} color="var(--text-muted)" />
            </button>

            {menuOpen && (
              <div style={{
                position: 'absolute', right: 0, top: 'calc(100% + 6px)',
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)',
                minWidth: 220, zIndex: 1000, overflow: 'hidden',
                animation: 'fadeIn .15s ease-out',
              }}>
                {/* user info header */}
                <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{user?.fullname || user?.username}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{user?.email}</div>
                  <div style={{ marginTop: 6, display: 'flex', gap: 6 }}>
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: 'rgba(139,92,246,0.15)', color: 'var(--accent-purple)', border: '1px solid rgba(139,92,246,0.3)' }}>
                      {user?.role}
                    </span>
                    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: 'rgba(16,185,129,0.12)', color: 'var(--accent-green)', border: '1px solid rgba(16,185,129,0.3)' }}>
                      {user?.status}
                    </span>
                  </div>
                </div>

                {/* actions */}
                {[
                  { icon: <KeyRound size={14} />, label: 'Ganti Password', action: () => { setMenuOpen(false); setShowPwd(true); } },
                  { icon: <LogOut size={14} />, label: 'Keluar', action: logout, danger: true },
                ].map(({ icon, label, action, danger }) => (
                  <button key={label} onClick={action}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      width: '100%', padding: '10px 16px',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: danger ? 'var(--accent-red)' : 'var(--text-secondary)',
                      fontSize: 13, textAlign: 'left', transition: 'background .12s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = danger ? 'rgba(239,68,68,0.08)' : 'var(--bg-card-hover)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    {icon} {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}